import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Trash2, Users, Clock, Gamepad2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ActiveGame {
    roomId: string;
    roomCode: string;
    phase: 'LOBBY' | 'SETUP' | 'PLAYING' | 'VERDICT';
    playerCount: number;
    createdAt: string;
}

interface ActiveGamesListProps {
    games: ActiveGame[];
    onAbandon: (roomId: string) => void;
}

const PHASE_LABELS: Record<string, string> = {
    'LOBBY': 'En attente',
    'SETUP': 'Configuration',
    'PLAYING': 'En cours',
    'VERDICT': 'Phase finale'
};

const PHASE_COLORS: Record<string, string> = {
    'LOBBY': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'SETUP': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'PLAYING': 'bg-green-500/20 text-green-400 border-green-500/30',
    'VERDICT': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const ActiveGamesList: React.FC<ActiveGamesListProps> = ({ games, onAbandon }) => {
    const navigate = useNavigate();
    const [confirmAbandon, setConfirmAbandon] = useState<string | null>(null);

    const handleResume = (roomCode: string) => {
        navigate(`/game?roomCode=${roomCode}`);
    };

    const handleAbandonClick = (roomId: string) => {
        setConfirmAbandon(roomId);
    };

    const confirmAbandonGame = (roomId: string) => {
        onAbandon(roomId);
        setConfirmAbandon(null);
    };

    const getTimeSince = (createdAt: string) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now.getTime() - created.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `Il y a ${diffMins} min`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        const diffDays = Math.floor(diffHours / 24);
        return `Il y a ${diffDays}j`;
    };

    if (games.length === 0) {
        return (
            <div className="mb-8">
                <h2 className="text-2xl font-serif font-bold text-cream-100 mb-4 flex items-center gap-3">
                    <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                    Parties en Cours
                </h2>
                <div className="text-center py-12 text-cream-200/40 border-2 border-dashed border-navy-800 rounded-xl">
                    <Gamepad2 size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Aucune partie en cours</p>
                    <p className="text-xs mt-2">Créez une nouvelle partie pour commencer !</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-8">
            <h2 className="text-2xl font-serif font-bold text-cream-100 mb-4 flex items-center gap-3">
                <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                Parties en Cours
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {games.map((game) => (
                    <motion.div
                        key={game.roomId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 rounded-xl p-5 hover:border-gold-500/50 transition-all shadow-lg"
                    >
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <div className="text-gold-400 font-mono font-bold text-lg tracking-wider">
                                    {game.roomCode}
                                </div>
                                <div className="text-cream-200/50 text-xs flex items-center gap-2 mt-1">
                                    <Clock size={12} />
                                    {getTimeSince(game.createdAt)}
                                </div>
                            </div>
                            <span className={`text-xs px-3 py-1 rounded-full border ${PHASE_COLORS[game.phase]}`}>
                                {PHASE_LABELS[game.phase]}
                            </span>
                        </div>

                        {/* Info */}
                        <div className="flex items-center gap-2 text-cream-200/70 text-sm mb-4">
                            <Users size={16} />
                            <span>{game.playerCount} joueur{game.playerCount > 1 ? 's' : ''}</span>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleResume(game.roomCode)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold transition-all shadow-lg"
                            >
                                <Play size={16} />
                                Reprendre
                            </button>
                            <button
                                onClick={() => handleAbandonClick(game.roomId)}
                                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 rounded-lg font-bold transition-all"
                                title="Abandonner la partie"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Confirmation Modal */}
            <AnimatePresence>
                {confirmAbandon && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setConfirmAbandon(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-navy-900 border border-red-500/50 rounded-xl p-6 max-w-md w-full shadow-2xl"
                        >
                            <h3 className="text-xl font-serif font-bold text-cream-100 mb-3">
                                Abandonner la partie ?
                            </h3>
                            <p className="text-cream-200/70 mb-6">
                                Êtes-vous sûr de vouloir abandonner cette partie ? Cette action est irréversible.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setConfirmAbandon(null)}
                                    className="flex-1 px-4 py-2 bg-navy-800 hover:bg-navy-700 text-cream-200 rounded-lg font-bold transition-all"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => confirmAbandonGame(confirmAbandon)}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg font-bold transition-all"
                                >
                                    Abandonner
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ActiveGamesList;
